import type { ZudokuConfig } from "zudoku";
import LanguageSwitcher from "./src/LanguageSwitcher";
import { ZoomableImage } from "./src/ZoomableImage";

const config: ZudokuConfig = {
  site: {
    logo: {
      src: { light: "/images/chatmo-logo-v2.png", dark: "/images/chatmo-logo-v2.png" },
      alt: "Chatmo",
      width: "140px",
    },
  },
  metadata: {
    title: "%s | Chatmo Documentation",
    description:
      "Official documentation for Chatmo Dashboard — multi-channel messaging, AI agents, broadcast campaigns, and more.",
    favicon: "/images/favicon.ico",
  },

  // ============================================================
  // MDX COMPONENT OVERRIDES
  // ------------------------------------------------------------
  // Zudoku has no built-in image lightbox (checked upstream up to
  // 0.86.0). Overriding `img` here -- rather than in each .mdx file
  // -- makes every screenshot across the docs open in a closable,
  // zoomable modal on click, site-wide. See src/ZoomableImage.tsx.
  // ============================================================
  mdx: {
    components: {
      img: ZoomableImage,
    },
  },

  // ============================================================
  // TOP-NAV SLOTS
  // ------------------------------------------------------------
  // Zudoku reserves named slots in its layout so consumers can
  // inject custom React without forking the chrome. The "side"
  // slot is the right edge of the top bar — we use it for the
  // language switcher (see src/LanguageSwitcher.tsx).
  // ============================================================
  slots: {
    "top-navigation-side": <LanguageSwitcher />,
  },

  // ============================================================
  // OPEN API REFERENCE
  // ------------------------------------------------------------
  // The chatmo-crm OpenAPI 3.0.3 spec lives in ./apis/chatmo-crm.json.
  // Zudoku parses it at build time, generates the /api-catalog tree
  // (tags become sidebar categories via x-tagGroups), and wires the
  // "Try it" playground against the servers listed in the spec.
  //
  // The spec itself is the source of truth — edit ./apis/chatmo-crm.json
  // (not the dashboard repo) to add or change endpoints. Keep it in
  // sync with chatmo-crm/docs/OPENAPI_DOCUMENTATION.md and
  // chatmo-crm/docs/postman/chatmo-open-api.postman_collection.json.
  //
  // `examplesLanguage` uses `shell` (Shiki's curl-aware grammar)
  // — Shiki has no language literally named `curl`, hence the
  // earlier crash on /api-reference/auth. We keep the user-facing
  // label as "cURL".
  //
  // The full language list mirrors what `@zudoku/httpsnippet` can
  // emit. We picked the ones an integrator is most likely to be
  // working in; trim or reorder if a particular language is noise.
  // ============================================================
  apis: {
    type: "file",
    input: "./apis/chatmo-crm.json",
    path: "api-reference",
    options: {
      examplesLanguage: "shell",
      supportedLanguages: [
        { value: "shell", label: "cURL" },
        { value: "javascript", label: "JavaScript" },
        { value: "node", label: "Node.js" },
        { value: "python", label: "Python" },
        { value: "go", label: "Go" },
        { value: "ruby", label: "Ruby" },
        { value: "php", label: "PHP" },
        { value: "java", label: "Java" },
        { value: "csharp", label: "C#" },
        { value: "swift", label: "Swift" },
        { value: "kotlin", label: "Kotlin" },
      ],
    },
  },

  navigation: [
    // ==================================================
    // DOCUMENTATION (English content)
    // --------------------------------------------------
    // English is the primary language tab. Indonesian
    // lives under a parallel "Bahasa Indonesia" tab below.
    // The language switcher in the top-nav-side slot is the
    // fast way to jump between them; the tabs themselves
    // are also clickable as a fallback / visual indicator
    // of which language is active.
    // ==================================================
    {
      type: "category",
      label: "Documentation",
      icon: "book-open",
      items: [
        { type: "filter", placeholder: "Filter documentation..." },

            // ── GETTING STARTED ──
            {
              type: "category",
              label: "Getting Started",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Getting Started",
                  icon: "rocket",
                  items: [
                    { type: "doc", file: "/en/getting-started/introduction", icon: "book-open" },
                    { type: "doc", file: "/en/getting-started/registration-and-login", icon: "log-in" },
                    { type: "doc", file: "/en/getting-started/onboarding", icon: "list-checks" },
                    { type: "doc", file: "/en/getting-started/quickstart", icon: "zap" },
                  ],
                },
                {
                  type: "category",
                  label: "Dashboard",
                  icon: "layout-dashboard",
                  items: [
                    { type: "doc", file: "/en/dashboard/overview", icon: "gauge" },
                  ],
                },
              ],
            },

            // ── AI & PLATFORM ──
            {
              type: "category",
              label: "AI & Platform",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Channels",
                  icon: "radio",
                  items: [
                    { type: "doc", file: "/en/channels/overview", icon: "compass" },
                    { type: "doc", file: "/en/channels/whatsapp", icon: "message-circle" },
                    { type: "doc", file: "/en/channels/whatsapp-business", icon: "briefcase" },
                    { type: "doc", file: "/en/channels/instagram", icon: "camera" },
                    { type: "doc", file: "/en/channels/facebook-messenger", icon: "message-square" },
                    { type: "doc", file: "/en/channels/telegram", icon: "send" },
                    { type: "doc", file: "/en/channels/web-chat", icon: "globe" },
                  ],
                },
                {
                  type: "category",
                  label: "WA Devices",
                  icon: "smartphone",
                  items: [
                    { type: "doc", file: "/en/devices/device-management", icon: "monitor-smartphone" },
                    { type: "doc", file: "/en/devices/add-new-device", icon: "plus-circle" },
                    { type: "doc", file: "/en/devices/monitor-and-reconnect", icon: "wifi" },
                  ],
                },
                {
                  type: "category",
                  label: "AI Agent",
                  icon: "bot",
                  badge: { label: "New", color: "purple" },
                  items: [
                    { type: "doc", file: "/en/ai-agent/overview", icon: "compass" },
                    { type: "doc", file: "/en/ai-agent/create-agent", icon: "plus-circle" },
                    {
                      type: "category",
                      label: "AI Agent Behaviour",
                      items: [
                        { type: "doc", file: "/en/ai-agent/ai-behaviors", icon: "brain" },
                        { type: "doc", file: "/en/ai-agent/handoff-prompts", icon: "arrow-right-left" },
                      ],
                    },
                    { type: "doc", file: "/en/ai-agent/knowledge-base", icon: "database" },
                    { type: "doc", file: "/en/ai-agent/auto-follow-up", icon: "clock", badge: { label: "New", color: "purple" } },
                    { type: "doc", file: "/en/ai-agent/tools-configuration", icon: "wrench" },
                    { type: "doc", file: "/en/ai-agent/testing", icon: "flask-conical" },
                  ],
                },
                {
                  type: "category",
                  label: "Workflow",
                  icon: "workflow",
                  badge: { label: "New", color: "purple" },
                  items: [
                    { type: "doc", file: "/en/workflow/overview", icon: "compass" },
                    { type: "doc", file: "/en/workflow/build-your-first-workflow", icon: "square-pen" },
                    { type: "doc", file: "/en/workflow/building-blocks", icon: "blocks" },
                    { type: "doc", file: "/en/workflow/templates", icon: "layout-template" },
                    { type: "doc", file: "/en/workflow/activate-on-channel", icon: "plug" },
                    { type: "doc", file: "/en/workflow/patterns", icon: "lightbulb" },
                  ],
                },
                {
                  type: "category",
                  label: "AI Tools Integration",
                  icon: "wrench",
                  badge: { label: "New", color: "purple" },
                  items: [
                    { type: "doc", file: "/en/ai-tools/ecommerce-order", icon: "shopping-bag" },
                    { type: "doc", file: "/en/ai-tools/check-ongkir", icon: "truck" },
                    { type: "doc", file: "/en/ai-tools/web-search", icon: "search" },
                  ],
                },
                {
                  type: "category",
                  label: "Human Agent",
                  icon: "headset",
                  items: [
                    { type: "doc", file: "/en/human-agent/team-management", icon: "users" },
                    { type: "doc", file: "/en/human-agent/roles-and-permissions", icon: "shield" },
                  ],
                },
              ],
            },

            // ── MESSAGING ──
            {
              type: "category",
              label: "Messaging",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Chat",
                  icon: "message-circle",
                  items: [
                    { type: "doc", file: "/en/chat/inbox-overview", icon: "inbox" },
                    { type: "doc", file: "/en/chat/conversation-management", icon: "messages-square" },
                    { type: "doc", file: "/en/chat/chat-features", icon: "sparkles" },
                  ],
                },
                {
                  type: "category",
                  label: "WhatsApp Call",
                  icon: "phone",
                  badge: { label: "New", color: "purple" },
                  items: [
                    { type: "doc", file: "/en/whatsapp-call/overview", icon: "compass" },
                    { type: "doc", file: "/en/whatsapp-call/enable-calls", icon: "phone-call" },
                    { type: "doc", file: "/en/whatsapp-call/making-and-answering-calls", icon: "phone-outgoing" },
                    { type: "doc", file: "/en/whatsapp-call/call-details", icon: "file-text" },
                    { type: "doc", file: "/en/whatsapp-call/call-history", icon: "history" },
                    { type: "doc", file: "/en/whatsapp-call/requirements-and-limits", icon: "shield" },
                  ],
                },
                {
                  type: "category",
                  label: "Contacts",
                  icon: "users",
                  items: [
                    { type: "doc", file: "/en/contacts/contact-management", icon: "contact" },
                    { type: "doc", file: "/en/contacts/import-contacts", icon: "upload" },
                    { type: "doc", file: "/en/contacts/segments", icon: "layers" },
                  ],
                },
                {
                  type: "category",
                  label: "Broadcast",
                  icon: "megaphone",
                  items: [
                    { type: "doc", file: "/en/broadcast/overview", icon: "compass" },
                    { type: "doc", file: "/en/broadcast/create-broadcast", icon: "plus-circle" },
                    { type: "doc", file: "/en/broadcast/recipient-list", icon: "list" },
                    { type: "doc", file: "/en/broadcast/templates", icon: "file-text" },
                    { type: "doc", file: "/en/broadcast/analytics", icon: "bar-chart-3" },
                  ],
                },
              ],
            },

            // ── INTEGRATION ──
            {
              type: "category",
              label: "Integration",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Integrations",
                  icon: "plug",
                  items: [
                    { type: "doc", file: "/en/integrations/meta-capi/overview", icon: "compass", label: "Meta Conversion API" },
                    {
                      type: "category",
                      label: "Webhook",
                      icon: "webhook",
                      badge: { label: "New", color: "purple" },
                      items: [
                        { type: "doc", file: "/en/integrations/webhook/overview", icon: "compass" },
                        { type: "doc", file: "/en/integrations/webhook/setup", icon: "plus-circle" },
                        { type: "doc", file: "/en/integrations/webhook/events-and-payloads", icon: "file-json" },
                        { type: "doc", file: "/en/integrations/webhook/security-and-reliability", icon: "shield" },
                        { type: "doc", file: "/en/integrations/webhook/testing-and-sample-code", icon: "flask-conical" },
                      ],
                    },
                  ],
                },
              ],
            },

            // ── OPERATIONS ──
            {
              type: "category",
              label: "Operations",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Orders",
                  icon: "shopping-cart",
                  items: [
                    { type: "doc", file: "/en/orders/overview", icon: "compass" },
                    { type: "doc", file: "/en/orders/create-order", icon: "plus-circle" },
                    { type: "doc", file: "/en/orders/products", icon: "package" },
                    { type: "doc", file: "/en/orders/shop-addresses", icon: "map-pin" },
                    { type: "doc", file: "/en/orders/order-management", icon: "clipboard-list" },
                    {
                      type: "category",
                      label: "Payment",
                      icon: "credit-card",
                      items: [
                        { type: "doc", file: "/en/orders/payment", icon: "zap", label: "Payment with Xendit" },
                        { type: "doc", file: "/en/orders/manual-payment", icon: "building-2", label: "Manual Payment" },
                      ],
                    },
                    { type: "doc", file: "/en/orders/invoices", icon: "file-text" },
                  ],
                },
                {
                  type: "category",
                  label: "Tickets",
                  icon: "ticket",
                  items: [
                    { type: "doc", file: "/en/tickets/overview", icon: "compass" },
                    { type: "doc", file: "/en/tickets/ticket-board", icon: "kanban" },
                    { type: "doc", file: "/en/tickets/create-and-manage", icon: "plus-circle" },
                    { type: "doc", file: "/en/tickets/ticket-templates", icon: "file-text" },
                  ],
                },
              ],
            },

            // ── ANALYTICS & REPORTS ──
            {
              type: "category",
              label: "Analytics & Reports",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Analytics",
                  icon: "bar-chart-3",
                  items: [
                    { type: "doc", file: "/en/analytics/reports-overview", icon: "pie-chart" },
                    { type: "doc", file: "/en/analytics/chat-analytics", icon: "trending-up" },
                  ],
                },
              ],
            },

            // ── ACCOUNT & BILLING ──
            {
              type: "category",
              label: "Account & Billing",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Settings",
                  icon: "settings",
                  items: [
                    { type: "doc", file: "/en/settings/profile", icon: "user" },
                    { type: "doc", file: "/en/settings/security", icon: "shield" },
                    { type: "doc", file: "/en/settings/subscription-and-billing", icon: "credit-card" },
                  ],
                },
                {
                  type: "category",
                  label: "Affiliate",
                  icon: "hand-coins",
                  items: [
                    { type: "doc", file: "/en/affiliate/overview", icon: "compass" },
                    { type: "doc", file: "/en/affiliate/commission-and-payout", icon: "wallet" },
                  ],
                },
              ],
            },
      ],
    },

    // ==================================================
    // BAHASA INDONESIA (hidden from the top bar)
    // --------------------------------------------------
    // Sibling of Documentation. `display: "hide"` keeps
    // this entry OUT of the top bar — the Language
    // dropdown in the top-nav-side slot is the only
    // surface that exposes the switch. Routes under /id/*
    // still resolve, and the sidebar renders normally
    // when the URL points here.
    // ==================================================
    {
      type: "category",
      label: "Bahasa Indonesia",
      icon: "globe",
      display: "hide",
      items: [
        { type: "filter", placeholder: "Filter dokumentasi..." },

            // ── MEMULAI ──
            {
              type: "category",
              label: "Memulai",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Memulai",
                  icon: "rocket",
                  items: [
                    { type: "doc", file: "/id/memulai/pengenalan", icon: "book-open" },
                    { type: "doc", file: "/id/memulai/registrasi-dan-login", icon: "log-in" },
                    { type: "doc", file: "/id/memulai/onboarding", icon: "list-checks" },
                    { type: "doc", file: "/id/memulai/panduan-cepat", icon: "zap" },
                  ],
                },
                {
                  type: "category",
                  label: "Dashboard",
                  icon: "layout-dashboard",
                  items: [
                    { type: "doc", file: "/id/dashboard/ringkasan", icon: "gauge" },
                  ],
                },
              ],
            },

            // ── AI & PLATFORM ──
            {
              type: "category",
              label: "AI & Platform",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Channel",
                  icon: "radio",
                  items: [
                    { type: "doc", file: "/id/channel/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/channel/whatsapp", icon: "message-circle" },
                    { type: "doc", file: "/id/channel/whatsapp-business", icon: "briefcase" },
                    { type: "doc", file: "/id/channel/instagram", icon: "camera" },
                    { type: "doc", file: "/id/channel/facebook-messenger", icon: "message-square" },
                    { type: "doc", file: "/id/channel/telegram", icon: "send" },
                    { type: "doc", file: "/id/channel/web-chat", icon: "globe" },
                  ],
                },
                {
                  type: "category",
                  label: "Perangkat WA",
                  icon: "smartphone",
                  items: [
                    { type: "doc", file: "/id/perangkat/manajemen-perangkat", icon: "monitor-smartphone" },
                    { type: "doc", file: "/id/perangkat/tambah-perangkat-baru", icon: "plus-circle" },
                    { type: "doc", file: "/id/perangkat/monitor-dan-reconnect", icon: "wifi" },
                  ],
                },
                {
                  type: "category",
                  label: "AI Agent",
                  icon: "bot",
                  badge: { label: "Baru", color: "purple" },
                  items: [
                    { type: "doc", file: "/id/ai-agent/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/ai-agent/buat-agent", icon: "plus-circle" },
                    {
                      type: "category",
                      label: "AI Agent Behaviour",
                      items: [
                        { type: "doc", file: "/id/ai-agent/perilaku-ai", icon: "brain" },
                        { type: "doc", file: "/id/ai-agent/handoff-prompt", icon: "arrow-right-left" },
                      ],
                    },
                    { type: "doc", file: "/id/ai-agent/basis-pengetahuan", icon: "database" },
                    { type: "doc", file: "/id/ai-agent/auto-follow-up", icon: "clock", badge: { label: "Baru", color: "purple" } },
                    { type: "doc", file: "/id/ai-agent/konfigurasi-tools", icon: "wrench" },
                    { type: "doc", file: "/id/ai-agent/pengujian", icon: "flask-conical" },
                  ],
                },
                {
                  type: "category",
                  label: "Workflow",
                  icon: "workflow",
                  badge: { label: "Baru", color: "purple" },
                  items: [
                    { type: "doc", file: "/id/workflow/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/workflow/membuat-workflow-pertama", icon: "square-pen" },
                    { type: "doc", file: "/id/workflow/komponen-workflow", icon: "blocks" },
                    { type: "doc", file: "/id/workflow/template", icon: "layout-template" },
                    { type: "doc", file: "/id/workflow/mengaktifkan-di-channel", icon: "plug" },
                    { type: "doc", file: "/id/workflow/contoh-penggunaan", icon: "lightbulb" },
                  ],
                },
                {
                  type: "category",
                  label: "AI Tools Integration",
                  icon: "wrench",
                  badge: { label: "Baru", color: "purple" },
                  items: [
                    { type: "doc", file: "/id/ai-tools/ecommerce-order", icon: "shopping-bag" },
                    { type: "doc", file: "/id/ai-tools/cek-ongkir", icon: "truck" },
                    { type: "doc", file: "/id/ai-tools/web-search", icon: "search" },
                  ],
                },
                {
                  type: "category",
                  label: "Agen Manusia",
                  icon: "headset",
                  items: [
                    { type: "doc", file: "/id/agen-manusia/manajemen-tim", icon: "users" },
                    { type: "doc", file: "/id/agen-manusia/peran-dan-izin", icon: "shield" },
                  ],
                },
              ],
            },

            // ── PERPESANAN ──
            {
              type: "category",
              label: "Perpesanan",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Chat",
                  icon: "message-circle",
                  items: [
                    { type: "doc", file: "/id/chat/ringkasan-inbox", icon: "inbox" },
                    { type: "doc", file: "/id/chat/manajemen-percakapan", icon: "messages-square" },
                    { type: "doc", file: "/id/chat/fitur-chat", icon: "sparkles" },
                  ],
                },
                {
                  type: "category",
                  label: "WhatsApp Call",
                  icon: "phone",
                  badge: { label: "Baru", color: "purple" },
                  items: [
                    { type: "doc", file: "/id/panggilan-whatsapp/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/panggilan-whatsapp/mengaktifkan-panggilan", icon: "phone-call" },
                    { type: "doc", file: "/id/panggilan-whatsapp/melakukan-menerima-panggilan", icon: "phone-outgoing" },
                    { type: "doc", file: "/id/panggilan-whatsapp/detail-panggilan", icon: "file-text" },
                    { type: "doc", file: "/id/panggilan-whatsapp/riwayat-panggilan", icon: "history" },
                    { type: "doc", file: "/id/panggilan-whatsapp/ketentuan-batasan", icon: "shield" },
                  ],
                },
                {
                  type: "category",
                  label: "Kontak",
                  icon: "users",
                  items: [
                    { type: "doc", file: "/id/kontak/manajemen-kontak", icon: "contact" },
                    { type: "doc", file: "/id/kontak/impor-kontak", icon: "upload" },
                    { type: "doc", file: "/id/kontak/segmen", icon: "layers" },
                  ],
                },
                {
                  type: "category",
                  label: "Broadcast",
                  icon: "megaphone",
                  items: [
                    { type: "doc", file: "/id/broadcast/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/broadcast/buat-broadcast", icon: "plus-circle" },
                    { type: "doc", file: "/id/broadcast/daftar-penerima", icon: "list" },
                    { type: "doc", file: "/id/broadcast/template", icon: "file-text" },
                    { type: "doc", file: "/id/broadcast/analitik", icon: "bar-chart-3" },
                  ],
                },
              ],
            },

            // ── INTEGRASI ──
            {
              type: "category",
              label: "Integrasi",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Integrasi",
                  icon: "plug",
                  items: [
                    { type: "doc", file: "/id/integrasi/meta-capi/ringkasan", icon: "compass", label: "Meta Conversion API" },
                    {
                      type: "category",
                      label: "Webhook",
                      icon: "webhook",
                      badge: { label: "Baru", color: "purple" },
                      items: [
                        { type: "doc", file: "/id/integrasi/webhook/ringkasan", icon: "compass" },
                        { type: "doc", file: "/id/integrasi/webhook/pengaturan", icon: "plus-circle" },
                        { type: "doc", file: "/id/integrasi/webhook/event-dan-payload", icon: "file-json" },
                        { type: "doc", file: "/id/integrasi/webhook/keamanan-dan-keandalan", icon: "shield" },
                        { type: "doc", file: "/id/integrasi/webhook/pengujian-dan-contoh-kode", icon: "flask-conical" },
                      ],
                    },
                  ],
                },
              ],
            },

            // ── OPERASIONAL ──
            {
              type: "category",
              label: "Operasional",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Pesanan",
                  icon: "shopping-cart",
                  items: [
                    { type: "doc", file: "/id/pesanan/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/pesanan/buat-pesanan", icon: "plus-circle" },
                    { type: "doc", file: "/id/pesanan/produk", icon: "package" },
                    { type: "doc", file: "/id/pesanan/alamat-toko", icon: "map-pin" },
                    { type: "doc", file: "/id/pesanan/manajemen-pesanan", icon: "clipboard-list" },
                    {
                      type: "category",
                      label: "Pembayaran",
                      icon: "credit-card",
                      items: [
                        { type: "doc", file: "/id/pesanan/pembayaran", icon: "zap", label: "Pembayaran dengan Xendit" },
                        { type: "doc", file: "/id/pesanan/pembayaran-manual", icon: "building-2", label: "Pembayaran Manual" },
                      ],
                    },
                    { type: "doc", file: "/id/pesanan/invoice", icon: "file-text" },
                  ],
                },
                {
                  type: "category",
                  label: "Tiket",
                  icon: "ticket",
                  items: [
                    { type: "doc", file: "/id/tiket/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/tiket/papan-tiket", icon: "kanban" },
                    { type: "doc", file: "/id/tiket/buat-dan-kelola", icon: "plus-circle" },
                    { type: "doc", file: "/id/tiket/template-tiket", icon: "file-text" },
                  ],
                },
              ],
            },

            // ── ANALITIK & LAPORAN ──
            {
              type: "category",
              label: "Analitik & Laporan",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Analitik",
                  icon: "bar-chart-3",
                  items: [
                    { type: "doc", file: "/id/analitik/ringkasan-laporan", icon: "pie-chart" },
                    { type: "doc", file: "/id/analitik/analitik-chat", icon: "trending-up" },
                  ],
                },
              ],
            },

            // ── AKUN & TAGIHAN ──
            {
              type: "category",
              label: "Akun & Tagihan",
              collapsible: false,
              items: [
                {
                  type: "category",
                  label: "Pengaturan",
                  icon: "settings",
                  items: [
                    { type: "doc", file: "/id/pengaturan/profil", icon: "user" },
                    { type: "doc", file: "/id/pengaturan/keamanan", icon: "shield" },
                    { type: "doc", file: "/id/pengaturan/langganan-dan-tagihan", icon: "credit-card" },
                  ],
                },
                {
                  type: "category",
                  label: "Afiliasi",
                  icon: "hand-coins",
                  items: [
                    { type: "doc", file: "/id/afiliasi/ringkasan", icon: "compass" },
                    { type: "doc", file: "/id/afiliasi/komisi-dan-pembayaran", icon: "wallet" },
                  ],
                },
              ],
            },
          ],
        },

    // ==================================================
    // API REFERENCE
    // --------------------------------------------------
    // Auto-rendered by Zudoku from ./apis/chatmo-crm.json.
    // The sidebar groups (Authentication, Inbox, Tickets,
    // E-commerce, ...) come from x-tagGroups in the spec.
    // ==================================================
    {
      type: "link",
      label: "API Reference",
      to: "/api-reference",
      icon: "code",
      badge: { label: "v1", color: "blue" },
    },

    // ==================================================
    // DEVELOPER GUIDES
    // --------------------------------------------------
    // Hand-written MDX that complements the auto-generated
    // API Reference (worked examples, flows). Kept LAST so
    // it sits at the far right of the top nav, next to
    // API Reference.
    // ==================================================
    {
      type: "category",
      label: "Developer Guides",
      icon: "code",
      items: [
        { type: "doc", file: "/en/developers/send-message-api", icon: "send" },
      ],
    },
  ],

  search: {
    type: "pagefind",
  },
  redirects: [{ from: "/", to: "/en/getting-started/introduction" }],
  docs: {
    files: "/pages/**/*.{md,mdx}",
    // IMPORTANT: keep this `true`. With llms.* enabled and publishMarkdown
    // off, zudoku 0.69.3 has a bug where the prerender cleanup
    // (node_modules/zudoku/dist/vite/prerender/prerender.js: `rm(info.filePath)`)
    // deletes the SOURCE .mdx files in /pages instead of the generated .md
    // copies in /dist. Setting publishMarkdown: true skips that cleanup
    // branch and preserves the source files.
    publishMarkdown: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
    },
  },
};

export default config;
